/**
 * `editorSnapshot` — the ONE value the toolbar projects, and the ONE thing worth
 * re-rendering for.
 *
 * ── WHY THIS EXISTS (measured, 2026-07-28) ──────────────────────────────────
 * `<EditorToolbarMount>` bumped a `selTick` counter from every `selectionchange`
 * event and re-read `editorHandle.activeFormat()` in its RENDER BODY. A CDP trace
 * of one 5.1 s drag across a paragraph on the live paginated canvas measured:
 *
 *     selectionchange events   122
 *     activeFormat() reads     244   (2x — React StrictMode double-invoke, dev)
 *     main-thread busy      1770.8 ms
 *     react-dom               1123.2 ms  ← 63% of ALL main-thread work
 *
 * The canvas's own selection painting (`onPointerMove`) cost 65.5 ms across the
 * same 120 events. The ribbon re-render cost 17x that. The wasm read itself was
 * 2.3 ms total (0.01 ms/call) — the expensive part was never the wasm call, it
 * was reconciling 41 command controls across 8 groups on every single event.
 *
 * ── THE IDENTITY, AND WHY IT IS NOT THE SELECTION RANGE ─────────────────────
 * The obvious gate is "did the selection range move?". It is WRONG here, twice:
 *
 *   · On the paginated/continuous CANVAS the DOM selection is permanently
 *     collapsed (the surface is a `<canvas>` + a hidden input; the real
 *     `{anchor,focus}` lives in the wasm core). A `document.getSelection()`
 *     signature is therefore CONSTANT there — gating on it would freeze the
 *     toolbar on the surface that is the default editor.
 *   · A range can move without changing anything the toolbar shows (dragging
 *     through plain text — the common case), and formatting can change without
 *     the range moving (applying a mark at a collapsed caret).
 *
 * So the identity is the SNAPSHOT THE TOOLBAR ACTUALLY PROJECTS: the active
 * format + the four enable gates + the live host probes. Structurally equal →
 * nothing the user can see would differ → no state update, and React re-renders
 * NOTHING. This is strictly more precise than a range compare in both directions.
 */

import type { ActiveFormat, EditorHostBridge } from "@norkron/web-elaborate-editor";

/** Everything `<EditorToolbarMount>` reads per selection tick, in one value. */
export interface EditorSnapshot {
  readonly active: ActiveFormat;
  readonly hasSelection: boolean;
  readonly inTable: boolean;
  readonly hasCellRange: boolean;
  /** True when the caret's table HAS a structural header row — what lets
   *  `table.header-toggle` render PRESSED and actually toggle the header OFF
   *  instead of only ever turning it on. A surface that cannot answer degrades to
   *  `false`, which reproduces exactly the previous turn-on-only behaviour. */
  readonly tableHeaderOn: boolean;
  /** LIVE host probes — `canUndo`/`canRedo`/`canPaste`. These are part of the
   *  snapshot because they gate the History + Paste buttons and they move
   *  WITHOUT the caret moving (an edit changes stack depth; a permission grant
   *  changes clipboard read). Dropping them from the comparator would trade the
   *  old lag for stale, dishonest buttons. */
  readonly undo: boolean;
  readonly redo: boolean;
  readonly clipboardRead: boolean;
}

/** The minimal handle surface a snapshot reads. Every member is optional exactly
 *  as `ElaborateEditorHandle` declares it — a surface that cannot answer degrades
 *  to the honest `false`, never to a DOM probe. */
export interface SnapshotSource {
  activeFormat(): ActiveFormat;
  hasSelection?(): boolean;
  inTable?(): boolean;
  hasCellRange?(): boolean;
  tableHeaderOn?(): boolean;
}

/** Read the live snapshot. Cheap: the four handle reads measured 2.3 ms across
 *  244 calls (0.01 ms each) on the live paginated canvas. */
export function readEditorSnapshot(
  handle: SnapshotSource,
  host: Partial<EditorHostBridge>,
): EditorSnapshot {
  return {
    active: handle.activeFormat(),
    hasSelection: handle.hasSelection?.() ?? false,
    inTable: handle.inTable?.() ?? false,
    hasCellRange: handle.hasCellRange?.() ?? false,
    tableHeaderOn: handle.tableHeaderOn?.() ?? false,
    undo: host.canUndo?.() ?? Boolean(host.undo),
    redo: host.canRedo?.() ?? Boolean(host.redo),
    clipboardRead: host.canPaste?.() ?? false,
  };
}

function setEql(a: ReadonlySet<unknown>, b: ReadonlySet<unknown>): boolean {
  if (a === b) return true;
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

/** Shallow structural equality over a plain record of scalars. Used for the
 *  small DTO leaves (`para`, `textEffects`, `charBorder`) — all of which are
 *  flat, optional-scalar shapes in the docz transport. */
function recordEql(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== "object" || typeof b !== "object") return false;
  const ka = Object.keys(a as object);
  const kb = Object.keys(b as object);
  if (ka.length !== kb.length) return false;
  for (const k of ka) {
    const va = (a as Record<string, unknown>)[k];
    const vb = (b as Record<string, unknown>)[k];
    if (va === vb) continue;
    // `para.tabs` is the one nested member — an array of flat stops.
    if (Array.isArray(va) && Array.isArray(vb)) {
      if (va.length !== vb.length) return false;
      for (let i = 0; i < va.length; i += 1) {
        if (!recordEql(va[i], vb[i])) return false;
      }
      continue;
    }
    if (typeof va === "object" && typeof vb === "object") {
      if (!recordEql(va, vb)) return false;
      continue;
    }
    return false;
  }
  return true;
}

/** True when the two active-format snapshots are indistinguishable to the
 *  toolbar — i.e. re-rendering would paint exactly the same 41 controls. */
export function activeFormatEql(a: ActiveFormat, b: ActiveFormat): boolean {
  if (a === b) return true;
  return (
    a.link === b.link &&
    a.color === b.color &&
    a.highlight === b.highlight &&
    a.fontFamily === b.fontFamily &&
    a.fontSize === b.fontSize &&
    a.underlineStyle === b.underlineStyle &&
    a.letterSpacing === b.letterSpacing &&
    a.kind === b.kind &&
    a.level === b.level &&
    a.emphasis === b.emphasis &&
    Boolean(a.painterArmed) === Boolean(b.painterArmed) &&
    setEql(a.marks, b.marks) &&
    recordEql(a.textEffects, b.textEffects) &&
    recordEql(a.charBorder, b.charBorder) &&
    recordEql(a.para, b.para)
  );
}

/** True when nothing the toolbar can render has changed. */
export function editorSnapshotEql(a: EditorSnapshot, b: EditorSnapshot): boolean {
  if (a === b) return true;
  return (
    a.hasSelection === b.hasSelection &&
    a.inTable === b.inTable &&
    a.hasCellRange === b.hasCellRange &&
    a.tableHeaderOn === b.tableHeaderOn &&
    a.undo === b.undo &&
    a.redo === b.redo &&
    a.clipboardRead === b.clipboardRead &&
    activeFormatEql(a.active, b.active)
  );
}
